import { Link } from 'react-router-dom';
import { LegalLayout } from '../components/LegalLayout';
import { SEO } from '../components/SEO';

export function Terms() {
  return (
    <LegalLayout title="Terms &amp; Conditions" lastUpdated="7 August 2026">
      <SEO
        title="Terms & Conditions — Dormant Accounts Filing"
        description="The terms you agree to when you use Dormant Accounts Filing to file dormant company accounts with Companies House."
        path="/terms"
      />
      <p>
        These terms govern your use of Dormant Accounts Filing ("the Service", "we", "us").
        By using the Service or paying for a filing, you agree to these terms.
      </p>

      <h2>1. What the Service does</h2>
      <p>
        Dormant Accounts Filing prepares and submits dormant company accounts (Companies House
        form AA02) to Companies House on your behalf. We act purely as a conduit between you
        and Companies House – we do not provide accountancy, tax, or legal advice.
      </p>
      <p>The Service is intended for:</p>
      <ul>
        <li>UK private limited companies that are <strong>dormant</strong> within the meaning of section 1169 of the Companies Act 2006.</li>
        <li>Filings under <strong>FRS 102 Section 1A</strong> (and the equivalent dormant exemptions under sections 480 and 476).</li>
        <li>Directors or other individuals authorised to file accounts for the company.</li>
      </ul>

      <h2>2. Pricing</h2>
      <p>
        Our fee is <strong>£20 per filing</strong>, paid once at the time of submission. We are
        currently below the UK VAT threshold, so no VAT is charged. There is no subscription and
        no recurring billing.
      </p>
      <p>
        Companies House does not charge for accepting AA02 filings, so £20 is the total cost to
        you. Late-filing penalties (if your accounts are overdue) are charged separately by
        Companies House and are not part of our fee.
      </p>

      <h2>3. Refund policy</h2>
      <p>
        We want you to be confident that our service either works or you get your money back,
        subject to the conditions below.
      </p>
      <ul>
        <li>
          <strong>If we are unable to file your accounts with Companies House for a reason on
          our side</strong> – for example, a technical error in the iXBRL document we prepare,
          or our service is unavailable to attempt the submission – we will refund the £20
          fee, <strong>less the non-refundable payment-processing fees charged by Stripe
          and any other platform fees</strong> we incur on the transaction. Stripe does not
          return its processing fee when a payment is refunded, so we are unable to return
          that portion of the £20 to you.
        </li>
        <li>
          <strong>If the filing is rejected because of incorrect information you supplied</strong>
          {' '}– for example, a wrong Companies House authentication code, an incorrect
          balance-sheet figure, or because the company does not qualify as dormant – the £20
          fee is <strong>non-refundable</strong>. However, you do not need to pay again to
          fix it: email us at{' '}
          <a href="mailto:contact.dormantfiling@gmail.com">contact.dormantfiling@gmail.com</a>
          {' '}with the corrected details (for example, the correct authentication code) and
          we will re-submit the filing for you at no extra charge.
        </li>
        <li>
          If after reasonable correction your filing still cannot be accepted by Companies
          House for a reason on our side, we will refund the £20 fee less Stripe and platform
          fees as set out above.
        </li>
      </ul>
      <p>
        Refunds are made to the original payment card via Stripe and typically arrive within
        5–10 working days.
      </p>
      <p>
        Because filing is a service that is performed at your request and substantially completed
        the moment we submit to Companies House, the 14-day "cooling-off" right under the
        Consumer Contracts Regulations 2013 does not apply once a successful filing has been made
        – by clicking "Pay" you expressly request immediate performance of the service.
      </p>

      <h2>4. What you are responsible for</h2>
      <p>By using the Service you confirm that:</p>
      <ul>
        <li>You are authorised to file accounts for the company in question (typically as a director or company secretary).</li>
        <li>
          The company was <strong>dormant for the entire financial period</strong> being filed
          (no significant accounting transactions other than the issue of shares to subscribers
          or fees paid to Companies House).
        </li>
        <li>The balance-sheet figures you confirm are accurate to the best of your knowledge.</li>
        <li>The Companies House authentication code you provide belongs to your company.</li>
        <li>You are not using the Service to facilitate fraud, money laundering, or any other unlawful purpose.</li>
      </ul>
      <p>
        You agree that any losses or penalties arising from incorrect information you supply
        (including filing accounts for a company that wasn't actually dormant) are your
        responsibility, not ours.
      </p>

      <h3 className="mt-6">4.1 Balance-sheet figures and prior-year comparatives</h3>
      <p>
        On the confirmation page, we display two sets of figures:
      </p>
      <ul>
        <li>
          <strong>Current-year figures</strong> that you enter yourself (share capital paid and
          unpaid, number of shares, current assets, creditors, and any other balance-sheet
          items).
        </li>
        <li>
          <strong>Prior-year comparative figures</strong> that we retrieve from Companies House&apos;s
          public register for your last filed set of accounts. These are shown as a convenience
          to help you spot obvious inconsistencies. It is <strong>your responsibility</strong>{' '}
          to review them before you submit, and to correct them if they do not reflect your
          company&apos;s actual prior position.
        </li>
      </ul>
      <p>
        By ticking the confirmation checkboxes and paying the £20 fee, you are formally
        confirming — on behalf of the company&apos;s directors — that <strong>all figures shown
        on the confirmation page are true and accurate</strong>, that the company qualifies for
        the dormant-accounts exemption, and that no material transaction has been omitted.
      </p>

      <h3 className="mt-6">4.2 Your confirmation counts as your electronic signature</h3>
      <p>
        Your combined actions — reviewing the confirmation page, ticking each statement box,
        entering your Companies House authentication code, and completing payment — constitute
        your <strong>electronic signature and declaration</strong> for the purposes of the
        Electronic Communications Act 2000 and section 414 of the Companies Act 2006. These
        actions have the same legal effect as if you had physically signed the accounts.
      </p>
      <p>
        You confirm that you take these actions <strong>freely, of your own accord</strong>,
        and not under the influence of any third party, and that you have the mental capacity
        and authority to bind the company to this filing.
      </p>

      <h3 className="mt-6">4.3 Changes after submission</h3>
      <p>
        Once we have submitted your accounts to Companies House and you have received our
        acceptance confirmation, the filing becomes part of the public register. If the
        company&apos;s underlying records are subsequently found to be different from the figures
        you submitted — whether because of a bookkeeping change, an amended prior year,
        additional transactions discovered later, or any other reason — <strong>we are not
        liable</strong> for the discrepancy or for any consequences of it.
      </p>
      <p>
        In the same way, if the accounts you submitted through us do not match the
        comparative or prior-year figures held on the Companies House register, or need to
        be amended for any reason, correcting or re-filing them is your responsibility (and
        that of your accountant or director, if applicable). We are happy to assist for a
        separate agreed fee, but we accept no automatic obligation to do so and no liability
        for the original submission having contained figures that later required correction.
      </p>

      <h2>5. Acceptance by Companies House</h2>
      <p>
        We do everything reasonable to ensure your filing is accepted, including:
      </p>
      <ul>
        <li>Producing FRS 102-compliant iXBRL.</li>
        <li>Validating the document against the relevant taxonomy before submission.</li>
        <li>Polling Companies House until we receive an accept or reject response.</li>
      </ul>
      <p>
        However, the final decision to accept or reject a filing rests with Companies House. We
        cannot guarantee acceptance in cases of incorrect data, authentication failures, or
        company-specific issues outside our control.
      </p>

      <h2>6. Authentication code &amp; data</h2>
      <p>
        See our <Link to="/privacy">Privacy Policy</Link> for a detailed explanation of how we
        handle your data – including how your Companies House authentication code is encrypted
        in memory, used once, and never persisted to disk.
      </p>

      <h2>7. Liability</h2>
      <p>
        Our maximum liability to you for any claim arising from your use of the Service is
        limited to the £20 filing fee. We will not be liable for indirect or consequential
        losses, including but not limited to: lost profits, late-filing penalties imposed by
        Companies House where the underlying delay was not caused by us, or third-party
        professional fees.
      </p>
      <p>
        Nothing in these terms excludes or limits any liability that cannot lawfully be excluded
        or limited under UK law (for example, liability for fraud or for death or personal
        injury caused by negligence).
      </p>

      <h2>8. Disclaimers</h2>
      <p>
        We are not accountants, auditors, or solicitors. We do not provide tax, accounting, or
        legal advice. If you are unsure whether your company qualifies as dormant, or whether
        dormant accounts are appropriate for your circumstances, consult a qualified
        professional first.
      </p>

      <h2>9. Suspension and refusal of service</h2>
      <p>
        We may refuse to file, or suspend an in-progress filing, if we reasonably suspect that
        the information supplied is materially inaccurate or that the filing would breach
        applicable law. If we refuse to file for any such reason, we will refund the £20 fee
        less Stripe and platform fees as set out in section 3.
      </p>

      <h2>10. Changes to these terms</h2>
      <p>
        We may update these terms from time to time. The version that applies to a given filing
        is the version that was published on the date you paid. Material changes will be noted
        by updating the "Last updated" date at the top of this page.
      </p>

      <h2>11. Governing law</h2>
      <p>
        These terms are governed by the laws of England and Wales. Any disputes are subject to
        the exclusive jurisdiction of the courts of England and Wales.
      </p>

      <h2>12. Contact</h2>
      <p>
        Questions about these terms?{' '}
        <a href="mailto:contact.dormantfiling@gmail.com">contact.dormantfiling@gmail.com</a>.
      </p>

      <p className="mt-10 text-sm text-slate-500">
        See also our <Link to="/privacy">Privacy Policy</Link>.
      </p>
    </LegalLayout>
  );
}
